//@flow
import React, { Component } from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import Input from 'common/components/core/input';
import range from 'lodash/range';
import Icon from 'common/components/core/icon';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';

import { SelectTypes } from 'common/components/core/select/Select';
import { Select } from 'common/components/core/select';

import theme from './PageChooser.scss';
import { translate } from 'react-i18next';
import type { TFunction } from 'react-i18next';
import { SizeEnum } from 'common/enums/UIEnums';
import _ from 'lodash';
import locationMaster from 'common/tests/masterManagement/locationMaster.json';
import coreIds from 'common/tests/core/coreIds.json';

const KeyEventHandlerInput = KeyEventHandler(Input);

type Props = {
    selectedPageIndex: number,
    totalPages: number,
    pageSize: number,
    onPageSizeChange: number => void,
    onPageChange: number => void,
    t: TFunction
};

type State = {
    goToValue?: number
};

const DOTS = '...';

const PAGE_OPTIONS = [
    { value: 15, label: 15 },
    { value: 25, label: 25 },
    { value: 50, label: 50 },
    { value: 100, label: 100 }
];

class PageChooser extends Component<Props, State> {
    static defaultProps = {
        pageSizeOptions: PAGE_OPTIONS
    };

    state: State = {};

    onNextClick = () => {
        const { selectedPageIndex, totalPages, onPageChange } = this.props;
        if (selectedPageIndex >= totalPages - 1) {
            //disabled
            return;
        }
        onPageChange(Math.min(totalPages - 1, selectedPageIndex + 1));
    };

    onBackClick = () => {
        const { selectedPageIndex, onPageChange } = this.props;
        if (selectedPageIndex <= 0) {
            //disabled
            return;
        }
        onPageChange(Math.max(0, selectedPageIndex - 1));
    };

    onPageNumberClick = (event: Event) => {
        // using currentTarget because the onClick is defined on the parent div
        // and sometimes you may have clicked on the inner span and hence target
        // would be that span and not div on which id is defined
        this.props.onPageChange(Number.parseInt(event.currentTarget.id));
    };

    onGoToPageClick = () => {
        const { goToValue } = this.state;
        const { totalPages, onPageChange } = this.props;
        if (
            typeof goToValue !== 'number' ||
            goToValue > totalPages ||
            goToValue < 1
        ) {
            // No need of this check, just kept it in case
            // some wierd cases kick in where the page number decreases
            // after input
            return;
        }
        onPageChange(goToValue - 1);
    };

    onGoToValueChange = (newValue: string) => {
        if (!newValue) {
            this.setState({
                goToValue: newValue
            });
            return;
        }
        const numericValue = Number.parseInt(newValue);
        if (numericValue < 1 || _.isNaN(numericValue)) {
            return;
        }
        this.setState({
            goToValue: numericValue
        });
    };

    onPageSizeChange = (value: { value: number, label: number }) => {
        const { onPageSizeChange } = this.props;
        onPageSizeChange(value.value);
    };

    computeNumericBubblesToRender(): Array<number | string> {
        const { selectedPageIndex, totalPages } = this.props;
        if (totalPages <= 7) {
            return [...range(totalPages)];
        }
        const lastPageIndex = totalPages - 1;
        if (selectedPageIndex <= 3) {
            // If page <=4, show all the first pages and the last page
            // [0, 1, 2, 3, 4, ..., 49]
            return [...range(5), DOTS, lastPageIndex];
        }
        if (selectedPageIndex >= totalPages - 4) {
            // [0, ...., 45, 46, 47, 48, 49]
            return [0, DOTS, ...range(totalPages - 5, totalPages)];
        }
        return [
            0,
            DOTS,
            selectedPageIndex - 1,
            selectedPageIndex,
            selectedPageIndex + 1,
            DOTS,
            lastPageIndex
        ];
    }

    renderNumbericBubble(
        index: number,
        isSelected: boolean = false,
        arrayIndex: number
    ) {
        // defining id on the parent div instead of span because
        // sometimes you may have clicked outside of the span
        // not using css hack to make span covering entire div
        // because sometimes it still might break due to future changes
        const { theme } = this.props;
        return (
            <div
                key={arrayIndex}
                className={classnames(theme.numbericBubble, {
                    [theme.selected]: isSelected
                })}
                id={index}
                onClick={this.onPageNumberClick}
            >
                <span className={theme.text}>{index + 1}</span>
            </div>
        );
    }

    threeDots = arrayIndex => (
        <span className={theme.threeDots} key={arrayIndex}>
            ...
        </span>
    );

    renderNumericSelectors() {
        const { selectedPageIndex } = this.props;
        const bubbleIndexesToRender = this.computeNumericBubblesToRender();
        return bubbleIndexesToRender.map(
            (bubbleIndex: number | string, arrayIndex: number) => {
                if (bubbleIndex === DOTS) {
                    return this.threeDots(arrayIndex);
                }
                if (typeof bubbleIndex !== 'number') {
                    throw new Error(
                        `expected number; received: ${bubbleIndex}`
                    );
                }
                return this.renderNumbericBubble(
                    bubbleIndex,
                    bubbleIndex === selectedPageIndex,
                    arrayIndex
                );
            }
        );
    }

    renderGoTo() {
        const { totalPages, selectedPageIndex, t, theme } = this.props;
        const { goToValue } = this.state;
        let validNumber = false;
        let validButBad = false;
        if (goToValue) {
            validNumber = true;
            if (goToValue > totalPages) {
                validNumber = false;
                validButBad = true;
            }
        }
        return (
            <div className={theme.goToPage}>
                <span className={theme.label}>{t('pageChooser.gotoPage')}</span>
                <KeyEventHandlerInput
                    testId={locationMaster.footer.pageInput}
                    className={theme.goToField}
                    type={'number'}
                    min={1}
                    step={1}
                    max={totalPages}
                    theme={theme}
                    placeholder={'--'}
                    value={goToValue}
                    onChange={this.onGoToValueChange}
                    onEnter={this.onGoToPageClick}
                />
                <div
                    testId={locationMaster.footer.pageChooserArrow}
                    className={classnames(theme.button, {
                        [theme.isValid]:
                            validNumber && selectedPageIndex + 1 !== goToValue,
                        [theme.isBadNumber]: validButBad
                    })}
                    onClick={validNumber ? this.onGoToPageClick : undefined}
                >
                    <Icon
                        className={theme.goToIcon}
                        theme={theme}
                        icon="nav-arrow-right"
                    />
                </div>
            </div>
        );
    }

    renderPageSizeSelector() {
        const { pageSize, t, theme, pageSizeOptions } = this.props;
        return (
            <div className={theme.pageSizeChooser}>
                <span className={theme.label}>{t('pageChooser.show')}</span>
                <Select
                    testId={locationMaster.footer.rowLimitSelect}
                    noMarginBottom
                    className={theme.selector}
                    selectType={SelectTypes.Outline}
                    selectSize={SizeEnum.Small}
                    options={pageSizeOptions}
                    value={pageSize}
                    onChange={this.onPageSizeChange}
                    isSearchable={false}
                    isClearable={false}
                />
            </div>
        );
    }

    render() {
        const { selectedPageIndex, totalPages, t, theme } = this.props;
        return (
            <div className={theme.pageChooserContainer}>
                {this.renderPageSizeSelector()}
                <div
                    className={theme.pageChooser}
                    data-test-id={coreIds.pageChooser}
                >
                    <span
                        className={classnames(theme.backAndNext, {
                            [theme.disabled]: selectedPageIndex === 0
                        })}
                        onClick={this.onBackClick}
                    >
                        <Icon
                            className={theme.symbol}
                            theme={theme}
                            icon="caret_down"
                        />
                        <span className={theme.text}>
                            {t('pageChooser.back')}
                        </span>
                    </span>
                    <div className={theme.bubbles}>
                        {this.renderNumericSelectors()}
                    </div>
                    <span
                        className={classnames(theme.backAndNext, {
                            [theme.disabled]:
                                selectedPageIndex === totalPages - 1
                        })}
                        onClick={this.onNextClick}
                    >
                        <span className={theme.text}>
                            {t('pageChooser.next')}
                        </span>
                        <Icon
                            className={theme.symbol}
                            theme={theme}
                            icon="caret_up"
                        />
                    </span>
                </div>
                {this.renderGoTo()}
            </div>
        );
    }
}

const PageChooserComponent = themr(
    'ThemedPageChooserComponent',
    theme
)(translate('entityManagement')(PageChooser));

export default PageChooserComponent;
